import { WorkflowElement } from "../../element"

const ELIGIBLE_TYPES = new Set(["app-event-occurred", "app-action"])

const VARIANT_BY_TYPE = {
  "app-event-occurred": { variant: "event", label: "Liquid variables" },
  "app-action": { variant: "action", label: "Variables from upstream" }
}

/**
 * A pill rendered on the node tile body that displays the count of available
 * VA context Liquid variables for the node. Hidden when the count is zero or
 * the node type is ineligible.
 *
 * Clicking the pill dispatches a `va-variables-popover:open` event on the
 * global `Workflow` element so that `VariablesPopoverPortal` can render the
 * popover anchored to the pill.
 *
 * Appended programmatically from `node.js#renderVariablesPill` (mirrors the
 * `AsyncStats` lifecycle).
 */
export class VariablesPill extends WorkflowElement {
  static get elementName() {
    return "workflow-variables-pill"
  }
  static get observedAttributes() {
    return ["data-type", "data-node-id", "data-graph-id"]
  }

  initialize() {
    this.fetched = false
    this.handleClick = this.handleClick.bind(this)
  }

  connectedCallback() {
    this.style.display = "none"
    this.classList.add("flex", "justify-center", "px-3", "pb-3")
    this.tryFetch()
  }

  attributeChangedCallback() {
    this.tryFetch()
  }

  tryFetch() {
    if (this.fetched) return
    const { type, nodeId, graphId } = this.dataset
    if (!type || !nodeId || !graphId) return
    if (!ELIGIBLE_TYPES.has(type)) return

    this.fetched = true
    fetch(`/workflows/graphs/${graphId}/va_context?node_id=${nodeId}`, {
      credentials: "include",
      headers: { Accept: "application/json" }
    })
      .then(response => (response.ok ? response.json() : null))
      .then(data => this.renderFromData(data))
      .catch(() => {
        // Silent failure — pill stays hidden.
      })
  }

  renderFromData(data) {
    const totalCount = data?.total_count ?? 0
    if (totalCount === 0) {
      this.style.display = "none"
      return
    }

    const { label } = VARIANT_BY_TYPE[this.dataset.type]
    this.innerHTML = `
      <button
        type="button"
        class="workflow-variables-pill bg-white hover:bg-gray-50 active:bg-gray-50 flex gap-1 items-center px-2 py-1 rounded"
      >
        <span class="font-medium text-xs text-gray-700">${label}</span>
        <span class="font-semibold border rounded-full px-2 py-px bg-gray-100 border-gray-100 text-gray-900 text-xs leading-4">${totalCount}</span>
      </button>
    `
    this.style.display = ""
    this.querySelector("button").addEventListener("click", this.handleClick)
  }

  handleClick(event) {
    event.stopPropagation()
    event.preventDefault()

    if (typeof Workflow === "undefined") return

    const { variant } = VARIANT_BY_TYPE[this.dataset.type]
    Workflow.dispatchEvent(
      new CustomEvent("va-variables-popover:open", {
        detail: {
          anchor: this,
          graphId: this.dataset.graphId,
          nodeId: this.dataset.nodeId,
          variant
        }
      })
    )
  }
}

customElements.get(VariablesPill.elementName) ||
  customElements.define(VariablesPill.elementName, VariablesPill)
